import { useEffect, useState, useCallback, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  Loader2, Plus, Pencil, Trash2, FileText, CheckCircle2, Clock,
  Send, Eye, Building2, Globe, Layers, CalendarDays, Euro, Tag, Mail, Users, Package, Zap, Download,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const PROJECT_TYPES = [
  { value: "site_vitrine", label: "Site vitrine" },
  { value: "application_web", label: "Application web" },
  { value: "seo", label: "SEO / Referencement" },
  { value: "maintenance", label: "Maintenance" },
  { value: "ecommerce", label: "E-commerce" },
  { value: "refonte", label: "Refonte" },
  { value: "branding", label: "Branding / Identite visuelle" },
];

const STATUS_CFG = {
  proposal: { label: "Proposition", color: "text-[#0099CC]", bg: "bg-[#0099CC]/10", icon: Send },
  approved: { label: "Approuvee", color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", icon: CheckCircle2 },
  signed: { label: "Signee", color: "text-[#10B981]", bg: "bg-[#10B981]/10", icon: FileText },
};

/* ===================== PROPOSAL FORM ===================== */
function ProposalForm({ clients, leads, bases, options, templates, initial, onSave, onCancel }) {
  const [form, setForm] = useState(() => {
    if (initial) {
      const selIds = (initial.selected_options || []).map(o => o.id);
      return {
        ...initial,
        source_type: initial.lead_id ? "lead" : "client",
        selectedOptionIds: selIds,
        extra_setup_fee: 0,
        extra_monthly_fee: 0,
      };
    }
    return {
      client_id: "", lead_id: "", source_type: "client",
      project_name: "", project_type: "site_vitrine", domain: "",
      pages_count: "", description: "",
      base_id: "", selectedOptionIds: [],
      discount_type: "none", discount_value: 0,
      extra_setup_fee: 0, extra_monthly_fee: 0,
      validity_days: 30, notes: "",
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const toggleOption = (optId) => {
    setForm(f => {
      const ids = f.selectedOptionIds.includes(optId)
        ? f.selectedOptionIds.filter(id => id !== optId)
        : [...f.selectedOptionIds, optId];
      return { ...f, selectedOptionIds: ids };
    });
  };

  // Computed prices
  const selectedBase = bases.find(b => b.id === form.base_id);
  const baseSetup = selectedBase ? selectedBase.setup_price : 0;
  const selectedOpts = options.filter(o => form.selectedOptionIds.includes(o.id));
  const optionsMonthly = selectedOpts.reduce((sum, o) => sum + o.monthly_price, 0);
  const rawSetup = baseSetup + Number(form.extra_setup_fee || 0);
  const rawMonthly = optionsMonthly + Number(form.extra_monthly_fee || 0);

  const dtype = form.discount_type;
  const dvalue = Number(form.discount_value || 0);
  let finalSetup = rawSetup, finalMonthly = rawMonthly;
  if (dtype === "percent") {
    finalSetup = Math.max(0, rawSetup * (1 - dvalue / 100));
    finalMonthly = Math.max(0, rawMonthly * (1 - dvalue / 100));
  } else if (dtype === "fixed") {
    finalSetup = Math.max(0, rawSetup - dvalue);
    finalMonthly = Math.max(0, rawMonthly - dvalue);
  }

  const submit = async (e) => {
    e.preventDefault();
    const hasClient = form.source_type === "client" && form.client_id;
    const hasLead = form.source_type === "lead" && form.lead_id;
    if (!hasClient && !hasLead) { toast.error("Selectionnez un client ou un lead"); return; }
    if (!form.project_name) { toast.error("Nom du projet requis"); return; }
    if (!form.base_id) { toast.error("Selectionnez une base de projet"); return; }
    setSaving(true);
    try {
      const payload = {
        client_id: form.source_type === "client" ? form.client_id : undefined,
        lead_id: form.source_type === "lead" ? form.lead_id : undefined,
        project_name: form.project_name,
        project_type: form.project_type,
        domain: form.domain,
        pages_count: form.pages_count ? Number(form.pages_count) : null,
        description: form.description,
        base_id: form.base_id,
        base_name: selectedBase?.name || "",
        base_setup_price: baseSetup,
        selected_options: selectedOpts.map(o => ({ id: o.id, name: o.name, monthly_price: o.monthly_price })),
        extra_setup_fee: Number(form.extra_setup_fee || 0),
        extra_monthly_fee: Number(form.extra_monthly_fee || 0),
        discount_type: form.discount_type,
        discount_value: Number(form.discount_value || 0),
        validity_days: Number(form.validity_days || 30),
        notes: form.notes,
      };
      await onSave(payload);
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="proposal-form">
      {/* Source type selector */}
      {!initial && (
        <div className="flex gap-2 mb-2">
          <Button type="button" size="sm"
            variant={form.source_type === "client" ? "default" : "outline"}
            onClick={() => set("source_type", "client")}
            className={form.source_type === "client" ? "bg-[#0099CC] text-white" : "border-[#2A3441] text-[#94A3B8]"}
            data-testid="source-type-client">
            <Building2 className="h-3.5 w-3.5 mr-1.5" />Client existant
          </Button>
          <Button type="button" size="sm"
            variant={form.source_type === "lead" ? "default" : "outline"}
            onClick={() => set("source_type", "lead")}
            className={form.source_type === "lead" ? "bg-[#10B981] text-white" : "border-[#2A3441] text-[#94A3B8]"}
            data-testid="source-type-lead">
            <Users className="h-3.5 w-3.5 mr-1.5" />Depuis un lead
          </Button>
        </div>
      )}

      {/* Template quick-apply */}
      {!initial && templates && templates.filter(t => t.is_active).length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-[#8B5CF6]" />
            <Label className="text-white font-semibold text-sm">Appliquer un modele</Label>
          </div>
          <div className="flex gap-2 flex-wrap">
            {templates.filter(t => t.is_active).map(t => (
              <button key={t.id} type="button"
                onClick={() => setForm(f => ({
                  ...f,
                  base_id: t.base_id,
                  selectedOptionIds: t.option_ids || [],
                  discount_type: t.discount_type || "none",
                  discount_value: t.discount_value || 0,
                }))}
                className="bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 text-[#8B5CF6] px-3 py-2 rounded-lg text-xs hover:bg-[#8B5CF6]/20 transition-colors"
                data-testid={`apply-template-${t.id}`}>
                <Zap className="h-3 w-3 inline mr-1" />{t.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">{form.source_type === "lead" ? "Lead" : "Client"}</Label>
          {form.source_type === "lead" && !initial ? (
            <select data-testid="proposal-lead-select" value={form.lead_id}
              onChange={e => {
                const lead = leads.find(l => l.id === e.target.value);
                set("lead_id", e.target.value);
                if (lead && !form.project_name) {
                  setForm(f => ({...f, lead_id: e.target.value, project_name: `${lead.project_type} - ${lead.name}`, project_type: lead.project_type || "site_vitrine"}));
                }
              }}
              className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10">
              <option value="">-- Selectionnez un lead --</option>
              {leads.filter(l => l.status !== "won" && l.status !== "lost").map(l => (
                <option key={l.id} value={l.id}>{l.name} ({l.email}) - {l.city}</option>
              ))}
            </select>
          ) : (
            <select data-testid="proposal-client-select" value={form.client_id}
              onChange={e => set("client_id", e.target.value)}
              className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10">
              <option value="">-- Selectionnez --</option>
              {clients.map(c => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          )}
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Nom du projet</Label>
          <Input data-testid="proposal-name-input" value={form.project_name}
            onChange={e => set("project_name", e.target.value)}
            className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Ex: Site vitrine LCB Services" />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Type de projet</Label>
          <select data-testid="proposal-type-select" value={form.project_type}
            onChange={e => set("project_type", e.target.value)}
            className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10">
            {PROJECT_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Domaine</Label>
          <Input data-testid="proposal-domain-input" value={form.domain}
            onChange={e => set("domain", e.target.value)}
            className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Ex: lcb-services.fr" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Nombre de pages</Label>
          <Input data-testid="proposal-pages-input" type="number" min="1" value={form.pages_count}
            onChange={e => set("pages_count", e.target.value)}
            className="bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="text-[#94A3B8]">Description</Label>
        <Input data-testid="proposal-desc-input" value={form.description}
          onChange={e => set("description", e.target.value)}
          className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Description du projet..." />
      </div>

      {/* ===== BASE DE PROJET (OBLIGATOIRE) ===== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-[#0099CC]" />
          <Label className="text-white font-semibold text-sm">Base de projet (obligatoire)</Label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {bases.filter(b => b.is_active).map(b => (
            <button key={b.id} type="button" onClick={() => set("base_id", b.id)}
              className={`p-3 rounded-lg border text-left transition-all ${
                form.base_id === b.id
                  ? "border-[#0099CC] bg-[#0099CC]/10"
                  : "border-[#2A3441] bg-[#0B0F19] hover:border-[#2A3441]/80"
              }`} data-testid={`base-option-${b.id}`}>
              <p className={`font-medium text-sm ${form.base_id === b.id ? "text-[#0099CC]" : "text-white"}`}>{b.name}</p>
              <p className="text-xs text-[#64748b] mt-0.5">{b.setup_price.toFixed(0)} EUR</p>
            </button>
          ))}
        </div>
      </div>

      {/* ===== OPTIONS ACTIVABLES ===== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Layers className="h-4 w-4 text-[#10B981]" />
          <Label className="text-white font-semibold text-sm">Options mensuelles</Label>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
          {options.filter(o => o.is_active).map(o => {
            const checked = form.selectedOptionIds.includes(o.id);
            return (
              <button key={o.id} type="button" onClick={() => toggleOption(o.id)}
                className={`p-3 rounded-lg border text-left transition-all flex items-center gap-3 ${
                  checked ? "border-[#10B981] bg-[#10B981]/10" : "border-[#2A3441] bg-[#0B0F19] hover:border-[#2A3441]/80"
                }`} data-testid={`option-toggle-${o.id}`}>
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 ${
                  checked ? "border-[#10B981] bg-[#10B981]" : "border-[#64748b]"
                }`}>
                  {checked && <CheckCircle2 className="h-3 w-3 text-white" />}
                </div>
                <div>
                  <p className={`font-medium text-sm ${checked ? "text-[#10B981]" : "text-white"}`}>{o.name}</p>
                  <p className="text-xs text-[#64748b]">+{o.monthly_price.toFixed(0)} EUR/mois</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* ===== REMISE + EXTRAS ===== */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Remise type</Label>
          <select data-testid="proposal-discount-type" value={form.discount_type}
            onChange={e => set("discount_type", e.target.value)}
            className="w-full bg-[#0B0F19] border border-[#2A3441] text-white rounded-md px-3 py-2 h-10">
            <option value="none">Aucune</option>
            <option value="percent">Pourcentage</option>
            <option value="fixed">Montant fixe</option>
          </select>
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Remise valeur</Label>
          <Input data-testid="proposal-discount-value" type="number" step="0.01" min="0" value={form.discount_value}
            onChange={e => set("discount_value", e.target.value)}
            className="bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Validite (jours)</Label>
          <Input data-testid="proposal-validity-input" type="number" min="1" value={form.validity_days}
            onChange={e => set("validity_days", e.target.value)}
            className="bg-[#0B0F19] border-[#2A3441] text-white" />
        </div>
        <div className="space-y-2">
          <Label className="text-[#94A3B8]">Notes internes</Label>
          <Input data-testid="proposal-notes-input" value={form.notes}
            onChange={e => set("notes", e.target.value)}
            className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Notes..." />
        </div>
      </div>

      {/* ===== LIVE PRICE PREVIEW ===== */}
      <div className="bg-[#0B0F19] border border-[#2A3441] rounded-xl p-5 space-y-3" data-testid="price-preview">
        <p className="text-white font-semibold text-sm flex items-center gap-2">
          <Euro className="h-4 w-4 text-[#0099CC]" />Recapitulatif des prix
        </p>
        <div className="space-y-2 text-sm">
          {selectedBase && (
            <div className="flex justify-between">
              <span className="text-[#94A3B8]">Base: {selectedBase.name}</span>
              <span className="text-white font-medium">{baseSetup.toFixed(0)} EUR</span>
            </div>
          )}
          {selectedOpts.map(o => (
            <div key={o.id} className="flex justify-between">
              <span className="text-[#94A3B8]">{o.name}</span>
              <span className="text-[#10B981]">+{o.monthly_price.toFixed(0)} EUR/mois</span>
            </div>
          ))}
          {dvalue > 0 && (
            <div className="flex justify-between text-[#F59E0B]">
              <span>Remise {dtype === "percent" ? `(${dvalue}%)` : ""}</span>
              <span>-{dtype === "percent" ? `${dvalue}%` : `${dvalue} EUR`}</span>
            </div>
          )}
          <div className="pt-2 border-t border-[#2A3441] space-y-1">
            <div className="flex justify-between">
              <span className="text-white font-semibold">Total Setup</span>
              <span className="text-[#0099CC] font-bold">{finalSetup.toFixed(0)} EUR</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white font-semibold">Total Mensuel</span>
              <span className="text-[#0099CC] font-bold">{finalMonthly.toFixed(0)} EUR/mois</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex gap-3 pt-2">
        <Button type="button" variant="outline" onClick={onCancel}
          className="border-[#2A3441] text-[#94A3B8]" data-testid="proposal-cancel-btn">Annuler</Button>
        <Button type="submit" disabled={saving}
          className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="proposal-save-btn">
          {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Enregistrer
        </Button>
      </div>
    </form>
  );
}

/* ===================== PROPOSAL DETAIL ===================== */
function ProposalDetail({ proposal, isAdmin, onBack, onRefresh }) {
  const { token } = useAuth();
  const [signing, setSigning] = useState(false);
  const [approving, setApproving] = useState(false);
  const [signerName, setSignerName] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  const typeLabel = PROJECT_TYPES.find(t => t.value === proposal.project_type)?.label || proposal.project_type;
  const cfg = STATUS_CFG[proposal.status] || STATUS_CFG.proposal;
  const Icon = cfg.icon;

  const approve = async () => {
    setApproving(true);
    try {
      await axios.post(`${API}/proposals/${proposal.id}/approve`);
      toast.success("Proposition approuvee");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setApproving(false); }
  };

  const sign = async () => {
    if (!signerName.trim()) { toast.error("Nom du signataire requis"); return; }
    setSigning(true);
    try {
      await axios.post(`${API}/proposals/${proposal.id}/sign`, { signer_name: signerName });
      toast.success("Proposition signee ! Projet cree.");
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSigning(false); }
  };

  const sendEmail = async () => {
    setSendingEmail(true);
    try {
      const res = await axios.post(`${API}/proposals/${proposal.id}/send-email`);
      toast.success(`Email envoye a ${res.data.to}`);
      onRefresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur envoi email"); }
    finally { setSendingEmail(false); }
  };

  const selOpts = proposal.selected_options || [];

  return (
    <div className="space-y-6" data-testid="proposal-detail">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack} className="text-[#94A3B8] hover:text-white" data-testid="back-proposals-btn">Retour</Button>
        <div className="flex-1">
          <h2 className="text-xl font-bold text-white">{proposal.project_name}</h2>
          <p className="text-[#0099CC] text-sm">{proposal.client_name} - {typeLabel}</p>
        </div>
        {isAdmin && proposal.status !== "signed" && (
          <Button onClick={sendEmail} disabled={sendingEmail} variant="outline" size="sm"
            className="border-[#0099CC]/30 text-[#0099CC] hover:bg-[#0099CC]/10 mr-2" data-testid="send-proposal-email-btn">
            {sendingEmail ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Mail className="h-3.5 w-3.5 mr-1.5" />}
            Envoyer par email
          </Button>
        )}
        <Button
          data-testid="download-proposal-pdf"
          variant="outline"
          size="sm"
          className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10 mr-2"
          onClick={() => {
            fetch(`${API}/proposals/${proposal.id}/pdf`, { headers: { Authorization: `Bearer ${token}` } })
              .then(r => r.blob())
              .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = `devis-${proposal.id.slice(0,8)}.pdf`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              });
          }}
        >
          <Download className="h-3.5 w-3.5 mr-1.5" />PDF
        </Button>
        <Badge className={`${cfg.bg} ${cfg.color} border-0`}><Icon className="h-3.5 w-3.5 mr-1" />{cfg.label}</Badge>
      </div>

      {proposal.email_sent_at && (
        <div className="bg-[#0099CC]/5 border border-[#0099CC]/20 rounded-xl p-3 flex items-center gap-2 text-sm">
          <Mail className="h-4 w-4 text-[#0099CC]" />
          <span className="text-[#94A3B8]">Email envoye le {new Date(proposal.email_sent_at).toLocaleDateString("fr-FR")} a {new Date(proposal.email_sent_at).toLocaleTimeString("fr-FR")}</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Project info */}
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 space-y-4">
          <h3 className="text-white font-semibold flex items-center gap-2"><Layers className="h-4 w-4 text-[#0099CC]" />Projet</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-[#64748b]">Type</span><span className="text-white">{typeLabel}</span></div>
            {proposal.domain && <div className="flex justify-between"><span className="text-[#64748b]">Domaine</span><span className="text-[#0099CC]">{proposal.domain}</span></div>}
            {proposal.pages_count && <div className="flex justify-between"><span className="text-[#64748b]">Pages</span><span className="text-white">{proposal.pages_count}</span></div>}
            {proposal.description && <div><p className="text-[#64748b]">Description</p><p className="text-[#94A3B8] mt-1">{proposal.description}</p></div>}
          </div>
        </div>

        {/* Pricing with base + options breakdown */}
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6 space-y-4" data-testid="proposal-pricing">
          <h3 className="text-white font-semibold flex items-center gap-2"><Euro className="h-4 w-4 text-[#0099CC]" />Tarification</h3>
          <div className="space-y-3 text-sm">
            {/* Base */}
            {proposal.base_name && (
              <div className="pb-2 border-b border-[#2A3441]">
                <p className="text-[#64748b] text-xs uppercase tracking-wider mb-1">Base</p>
                <div className="flex justify-between">
                  <span className="text-white">{proposal.base_name}</span>
                  <span className="text-white font-bold">{(proposal.base_setup_price || 0).toLocaleString("fr-FR")} EUR</span>
                </div>
              </div>
            )}

            {/* Options */}
            {selOpts.length > 0 && (
              <div className="pb-2 border-b border-[#2A3441]">
                <p className="text-[#64748b] text-xs uppercase tracking-wider mb-1">Options selectionnees</p>
                {selOpts.map((o, i) => (
                  <div key={i} className="flex justify-between py-0.5">
                    <span className="text-[#94A3B8]">{o.name}</span>
                    <span className="text-[#10B981]">+{(o.monthly_price || 0).toFixed(0)} EUR/mois</span>
                  </div>
                ))}
              </div>
            )}

            {/* Discount */}
            {proposal.discount_value > 0 && (
              <div className="flex justify-between text-[#F59E0B]">
                <span>Remise commerciale</span>
                <span>{proposal.discount_type === "percent" ? `-${proposal.discount_value}%` : `-${proposal.discount_value} EUR`}</span>
              </div>
            )}

            {/* Totals */}
            <div className="pt-2 border-t border-[#2A3441] space-y-2">
              <div className="flex justify-between">
                <span className="text-white font-semibold">Total Setup</span>
                <div className="text-right">
                  <span className="text-[#0099CC] font-bold text-lg">{(proposal.final_setup_fee || 0).toLocaleString("fr-FR")} EUR</span>
                  {proposal.discount_value > 0 && <span className="text-xs text-[#64748b] line-through ml-2">{(proposal.setup_fee || 0).toLocaleString("fr-FR")} EUR</span>}
                </div>
              </div>
              <div className="flex justify-between">
                <span className="text-white font-semibold">Total Mensuel</span>
                <div className="text-right">
                  <span className="text-[#0099CC] font-bold text-lg">{(proposal.final_monthly_fee || 0).toLocaleString("fr-FR")} EUR/mois</span>
                  {proposal.discount_value > 0 && <span className="text-xs text-[#64748b] line-through ml-2">{(proposal.monthly_fee || 0).toLocaleString("fr-FR")} EUR</span>}
                </div>
              </div>
            </div>

            <div className="pt-2 border-t border-[#2A3441] flex justify-between">
              <span className="text-[#64748b]">Validite</span>
              <span className="text-white">{proposal.valid_until ? new Date(proposal.valid_until).toLocaleDateString("fr-FR") : `${proposal.validity_days}j`}</span>
            </div>
          </div>
          <p className="text-[10px] text-[#64748b]">TVA non applicable, art. 293 B du CGI</p>
        </div>
      </div>

      {/* Actions */}
      {proposal.status === "proposal" && !isAdmin && (
        <div className="bg-[#0099CC]/5 border border-[#0099CC]/20 rounded-xl p-6">
          <h3 className="text-white font-semibold mb-3">Accepter cette proposition</h3>
          <p className="text-sm text-[#94A3B8] mb-4">En acceptant, vous confirmez votre interet pour cette proposition.</p>
          <Button onClick={approve} disabled={approving} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="approve-proposal-btn">
            {approving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}Accepter la proposition
          </Button>
        </div>
      )}

      {(proposal.status === "proposal" || proposal.status === "approved") && !isAdmin && (
        <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-xl p-6" data-testid="sign-section">
          <h3 className="text-white font-semibold mb-3">Signer le contrat</h3>
          <p className="text-sm text-[#94A3B8] mb-4">En signant, vous acceptez les termes du contrat et lancez le projet.</p>
          <div className="flex items-end gap-3">
            <div className="flex-1 space-y-1">
              <Label className="text-[#94A3B8] text-xs">Nom complet du signataire</Label>
              <Input data-testid="signer-name-input" value={signerName} onChange={e => setSignerName(e.target.value)}
                className="bg-[#0B0F19] border-[#2A3441] text-white" placeholder="Prenom Nom" />
            </div>
            <Button onClick={sign} disabled={signing} className="bg-[#10B981] hover:bg-[#0d9668] text-white" data-testid="sign-proposal-btn">
              {signing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}Signer
            </Button>
          </div>
        </div>
      )}

      {proposal.status === "signed" && (
        <div className="bg-[#10B981]/5 border border-[#10B981]/20 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle2 className="h-5 w-5 text-[#10B981]" />
          <div>
            <p className="text-white font-medium">Contrat signe</p>
            {proposal.signed_at && <p className="text-xs text-[#94A3B8]">Signe le {new Date(proposal.signed_at).toLocaleDateString("fr-FR")}</p>}
          </div>
        </div>
      )}

      {isAdmin && proposal.notes && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-4">
          <p className="text-xs text-[#64748b] uppercase tracking-wider mb-1">Notes internes</p>
          <p className="text-[#94A3B8] text-sm">{proposal.notes}</p>
        </div>
      )}
    </div>
  );
}

/* ===================== MAIN PAGE ===================== */
export default function ProposalsPage() {
  const { isAdmin } = useAuth();
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [leads, setLeads] = useState([]);
  const [bases, setBases] = useState([]);
  const [options, setOptions] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [filter, setFilter] = useState("all");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = filter !== "all" ? `?status=${filter}` : "";
      const [pRes, cRes, lRes, bRes, oRes, tRes] = await Promise.all([
        axios.get(`${API}/proposals${statusParam}`),
        isAdmin ? axios.get(`${API}/clients`) : Promise.resolve({ data: [] }),
        isAdmin ? axios.get(`${API}/leads`) : Promise.resolve({ data: [] }),
        axios.get(`${API}/proposal-bases`),
        axios.get(`${API}/proposal-options`),
        axios.get(`${API}/proposal-templates`),
      ]);
      setProposals(pRes.data);
      setClients(cRes.data);
      setLeads(lRes.data);
      setBases(bRes.data);
      setOptions(oRes.data);
      setTemplates(tRes.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, [filter, isAdmin]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const create = async (data) => {
    await axios.post(`${API}/proposals`, data);
    toast.success("Proposition creee"); setCreating(false); await fetchData();
  };
  const update = async (data) => {
    await axios.put(`${API}/proposals/${editing.id}`, data);
    toast.success("Proposition mise a jour"); setEditing(null); await fetchData();
  };
  const remove = async (id) => {
    if (!window.confirm("Supprimer cette proposition ?")) return;
    await axios.delete(`${API}/proposals/${id}`);
    toast.success("Proposition supprimee"); await fetchData();
  };

  if (viewing) {
    return (
      <div className="space-y-6 animate-fade-in" data-testid="proposals-page">
        <ProposalDetail proposal={viewing} isAdmin={isAdmin}
          onBack={() => { setViewing(null); fetchData(); }}
          onRefresh={async () => {
            try {
              const res = await axios.get(`${API}/proposals/${viewing.id}`);
              setViewing(res.data);
            } catch { setViewing(null); }
            await fetchData();
          }} />
      </div>
    );
  }

  const counts = {
    all: proposals.length,
    proposal: proposals.filter(p => p.status === "proposal").length,
    approved: proposals.filter(p => p.status === "approved").length,
    signed: proposals.filter(p => p.status === "signed").length,
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="proposals-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">{isAdmin ? "Propositions" : "Mes Propositions"}</h1>
          <p className="text-[#94A3B8] mt-1">{proposals.length} proposition{proposals.length > 1 ? "s" : ""}</p>
        </div>
        {isAdmin && !creating && !editing && (
          <Button onClick={() => setCreating(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white" data-testid="new-proposal-btn">
            <Plus className="h-4 w-4 mr-2" />Nouvelle proposition
          </Button>
        )}
      </div>

      <div className="flex gap-2 flex-wrap">
        {[
          { key: "all", label: `Toutes (${counts.all})` },
          { key: "proposal", label: `En attente (${counts.proposal})` },
          { key: "approved", label: `Approuvees (${counts.approved})` },
          { key: "signed", label: `Signees (${counts.signed})` },
        ].map(f => (
          <Button key={f.key} variant={filter === f.key ? "default" : "outline"} size="sm"
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? "bg-[#0099CC] text-white" : "border-[#2A3441] text-[#94A3B8]"}
            data-testid={`filter-${f.key}`}>{f.label}</Button>
        ))}
      </div>

      {creating && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Nouvelle proposition</h3>
          <ProposalForm clients={clients} leads={leads} bases={bases} options={options} templates={templates}
            onSave={create} onCancel={() => setCreating(false)} />
        </div>
      )}
      {editing && (
        <div className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-6">
          <h3 className="text-white font-semibold mb-4">Modifier la proposition</h3>
          <ProposalForm clients={clients} leads={leads} bases={bases} options={options} templates={templates}
            initial={editing} onSave={update} onCancel={() => setEditing(null)} />
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" /></div>
      ) : proposals.length === 0 ? (
        <div className="text-center py-16 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <FileText className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucune proposition</h3>
          <p className="text-[#94A3B8]">{isAdmin ? "Creez votre premiere proposition commerciale." : "Vos propositions apparaitront ici."}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {proposals.map(p => {
            const typeLbl = PROJECT_TYPES.find(t => t.value === p.project_type)?.label || p.project_type;
            const sc = STATUS_CFG[p.status] || STATUS_CFG.proposal;
            const SIcon = sc.icon;
            return (
              <div key={p.id} className="bg-[#151B2B] border border-[#2A3441] rounded-xl p-5 hover:border-[#0099CC]/30 transition-colors"
                data-testid={`proposal-card-${p.id}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${sc.bg} shrink-0`}>
                    <SIcon className={`h-5 w-5 ${sc.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-white font-semibold">{p.project_name}</p>
                      <Badge className={`${sc.bg} ${sc.color} border-0 text-xs`}>{sc.label}</Badge>
                      {p.base_name && <Badge className="bg-[#2A3441] text-[#94A3B8] border-0 text-xs">{p.base_name}</Badge>}
                      {p.lead_id && <Badge className="bg-[#10B981]/10 text-[#10B981] border-0 text-xs">Via Lead</Badge>}
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-[#64748b]">
                      <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{p.client_name}</span>
                      {p.domain && <span className="flex items-center gap-1"><Globe className="h-3 w-3" />{p.domain}</span>}
                      <span className="flex items-center gap-1"><CalendarDays className="h-3 w-3" />Valide jusqu'au {p.valid_until ? new Date(p.valid_until).toLocaleDateString("fr-FR") : "N/A"}</span>
                      {(p.selected_options || []).length > 0 && (
                        <span className="flex items-center gap-1"><Layers className="h-3 w-3" />{(p.selected_options || []).length} option{(p.selected_options || []).length > 1 ? "s" : ""}</span>
                      )}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-white font-bold">{(p.final_monthly_fee || 0).toLocaleString("fr-FR")} EUR<span className="text-[#64748b] font-normal">/mois</span></p>
                    {(p.final_setup_fee || 0) > 0 && <p className="text-xs text-[#64748b]">+ Setup {(p.final_setup_fee || 0).toLocaleString("fr-FR")} EUR</p>}
                  </div>
                </div>
                <div className="mt-3 flex gap-2 justify-end">
                  <Button variant="outline" size="sm" onClick={() => setViewing(p)}
                    className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid={`view-proposal-${p.id}`}>
                    <Eye className="h-3.5 w-3.5 mr-1.5" />Voir
                  </Button>
                  {isAdmin && p.status !== "signed" && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => { setCreating(false); setEditing(p); }}
                        className="border-[#2A3441] text-[#94A3B8] hover:text-white" data-testid={`edit-proposal-${p.id}`}>
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />Modifier
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => remove(p.id)}
                        className="border-[#2A3441] text-[#EF4444] hover:bg-[#EF4444]/10" data-testid={`delete-proposal-${p.id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
